import { useQuery } from '@tanstack/react-query'
import { useContext } from 'react'
import type { ReactNode } from 'react'

import { QUERY_KEY } from '../../../constants'
import type { components } from '../../../constants/schema'

import { downloadAttachment, toast } from '../../../utils'

import { rootStore } from '../../../provider/StoreProvider'
import { ModalContext } from '../../../provider/ModalProvider'

import { useNavigate } from '../../../hooks/useNavigate'

import { getUserProductDetail } from '../../../api/product'

import { ColumnItem, RowBetween } from '../../../components/common'
import { Button } from '../../../components/button'
import { Card } from '../../../components/card/Card'
import { Text } from '../../../components/text'
import { DivideLine } from '../../../components/divide'
import { CancelProductConfirmModal } from '../modals/CancelProductConfirmModal'
import { RowData } from './RowData'

import { ReactComponent as DownloadIcon } from '../../../assets/ico/common/download_18.svg'

interface ProductInfoProps {
  id: number
  type: string
}

const downloadProductFile = async (
  activeProductSale: components['schemas']['ProductSaleDto'] | undefined,
  type: components['schemas']['ProductSaleFileItemRelationDto']['productSaleFileItemType'],
) => {
  const { commonStore } = rootStore

  if (typeof activeProductSale === 'undefined' || !activeProductSale.files?.length) {
    toast('문서를 불러오지 못했습니다.', undefined, false)
    return
  }

  const fileItem = activeProductSale.files.find(
    ({ productSaleFileItemType }) => productSaleFileItemType === type,
  )?.fileItem

  if (!fileItem || !fileItem.filePath) {
    toast('문서를 불러오지 못했습니다.', undefined, false)
    return
  }

  const { filePath, filename } = fileItem

  commonStore.setIsLoading(true)

  await downloadAttachment(filePath, filename)

  setTimeout(() => {
    commonStore.setIsLoading(false)
  }, 500)
}

const DownloadButton = ({ children, onClick }: { children: ReactNode; onClick?: () => void }) => {
  return (
    <Button type="dark" size="compact" block={true} onClick={onClick}>
      <Text size={14} weight={500} color="text_white">
        {children}
      </Text>
      <DownloadIcon />
    </Button>
  )
}

export function ProductInfo({ id, type }: ProductInfoProps) {
  const {
    data: userProductDetail,
    isPending,
    isError,
  } = useQuery({
    queryKey: [QUERY_KEY.userProduct, id],
    queryFn: async () => await getUserProductDetail(id),
  })

  if (isPending || isError) return null

  switch (type) {
    case 'SALE': // 청약 신청
      return <SaleInfo {...userProductDetail} />
    case 'SALE_COMPLETED': // 배정 확정
      return <AllocationInfo {...userProductDetail} />
    case 'CANCEL_OR_REFUND': // 청약 취소 혹은 환불
      return <></>
    case 'SELL': // 매각 중
      return <SellInfo {...userProductDetail} />
    case 'TO_BE_SETTLEMENT': // 정산 예정
      return <SettlementInfo {...userProductDetail} />
    case 'SETTLEMENT_FINISH': // 정산 완료
      return <CompleteSettlementInfo {...userProductDetail} />
    default:
      return <></>
  }
}

/**
 * 청약 신청
 * @param UserProductDto
 */
const SaleInfo = ({
  id,
  balance,
  offerPrice,
  subscriptionShares,
  cancelable,
  product: { id: productId, storagelocation, activeProductSale },
}: components['schemas']['UserProductDto']) => {
  const { openModal, closeModal } = useContext(ModalContext)

  const navigate = useNavigate()

  const openCancelModal = () =>
    openModal({
      id: 'CANCEL_PRODUCT',
      Component: () => (
        <CancelProductConfirmModal
          userProductId={id}
          productId={productId}
          onDismiss={() => {
            closeModal('CANCEL_PRODUCT')
            toast('작품 청약이 취소되었습니다.')
            navigate(-1)
          }}
        />
      ),
    })

  return (
    <>
      <RowData title="총 신청 금액" content={`${balance?.toLocaleString()}원`} />
      <RowData title="주당 공모가 액" content={`${offerPrice?.toLocaleString()}원`} />
      <RowData title="청약 신청 수" content={`${subscriptionShares?.toLocaleString()}주`} />
      <RowData title="작품 위치" content={storagelocation ?? '-'} />
      <DivideLine />
      {/* TODO: 파일 타입 filter */}
      <RowBetween $gap={8}>
        {activeProductSale?.files
          ?.filter(
            ({ productSaleFileItemType }) =>
              productSaleFileItemType === 'DOCS_INVESTOR_MANUAL' ||
              productSaleFileItemType === 'DOCS_INVESTMENT_CONTRACT',
          )
          .map(({ fileItemId, productSaleFileItemLabel, productSaleFileItemType }) => {
            const download = () => downloadProductFile(activeProductSale, productSaleFileItemType)
            return (
              <DownloadButton key={fileItemId} onClick={download}>
                {productSaleFileItemLabel}
              </DownloadButton>
            )
          })}
      </RowBetween>
      {/* TODO: 청약 취소 후 보유자산 리스트로 replace */}
      {cancelable && (
        <Button type="default" size="large" onClick={openCancelModal}>
          청약 취소
        </Button>
      )}
    </>
  )
}

/**
 * 배정 확정
 * @param UserProductDto
 */
const AllocationInfo = ({
  balance,
  offerPrice,
  subscriptionShares,
  totalShares,
  equalAllocatedShares,
  proRataAllocatedShares,
  unallocatedShares,
  product: { storagelocation, activeProductSale },
}: components['schemas']['UserProductDto']) => {
  return (
    <>
      <RowData title="배정 금액" content={`${balance?.toLocaleString()}원`} />
      <RowData title="주당 공모가 액" content={`${offerPrice?.toLocaleString()}원`} />
      <RowData title="청약 신청 수" content={`${subscriptionShares?.toLocaleString()}주`} />
      <ColumnItem $gap={8}>
        <RowData title="배정 수" content={`${totalShares?.toLocaleString()}주`} />
        <Card $direction="column" $gap={8} $padding="16px" $radius={8} $background="gray_1">
          <RowData
            title={{ value: '• 균등 배정 수', size: 14 }}
            content={{ value: `${equalAllocatedShares?.toLocaleString()}주`, size: 14 }}
          />
          <RowData
            title={{ value: '• 비례 배정 수', size: 14 }}
            content={{ value: `${proRataAllocatedShares?.toLocaleString()}주`, size: 14 }}
          />
        </Card>
      </ColumnItem>
      <RowData title="환불 수" content={`${unallocatedShares?.toLocaleString()}주`} />
      <RowData title="작품 위치" content={storagelocation ?? '-'} />
      <DivideLine />
      <ColumnItem $gap={8}>
        <RowBetween $gap={8}>
          {activeProductSale?.files
            ?.filter(
              ({ productSaleFileItemType }) =>
                productSaleFileItemType === 'DOCS_INVESTOR_MANUAL' ||
                productSaleFileItemType === 'DOCS_INVESTMENT_CONTRACT',
            )
            .map(({ fileItemId, productSaleFileItemLabel, productSaleFileItemType }) => {
              const download = () => downloadProductFile(activeProductSale, productSaleFileItemType)
              return (
                <DownloadButton key={fileItemId} onClick={download}>
                  {productSaleFileItemLabel}
                </DownloadButton>
              )
            })}
        </RowBetween>
        <DownloadButton onClick={() => downloadProductFile(activeProductSale, 'DOCS_INVESTOR_LIST')}>
          투자자 명부
        </DownloadButton>
      </ColumnItem>
    </>
  )
}

/**
 * 매각 중
 * @param UserProductDto
 */
const SellInfo = ({
  id,
  balance,
  offerPrice,
  subscriptionShares,
  totalShares,
  equalAllocatedShares,
  proRataAllocatedShares,
  unallocatedShares,
  product: { storagelocation, activeProductSale },
}: components['schemas']['UserProductDto']) => {
  return (
    <>
      <RowData title="배정 금액" content={`${balance?.toLocaleString()}원`} />
      <RowData title="주당 공모가 액" content={`${offerPrice?.toLocaleString()}원`} />
      <RowData title="청약 신청 수" content={`${subscriptionShares?.toLocaleString()}주`} />
      <ColumnItem $gap={8}>
        <RowBetween>
          <Text size={16} color="text_secondary">
            배정 수
          </Text>
          <Text size={16} weight={500}>
            {totalShares?.toLocaleString()}주
          </Text>
        </RowBetween>
        <Card $direction="column" $gap={8} $padding="16px" $radius={8} $background="gray_1">
          <RowData
            title={{ value: '• 균등 배정 수', size: 14 }}
            content={{ value: `${equalAllocatedShares?.toLocaleString()}주`, size: 14 }}
          />
          <RowData
            title={{ value: '• 비례 배정 수', size: 14 }}
            content={{ value: `${proRataAllocatedShares?.toLocaleString()}주`, size: 14 }}
          />
        </Card>
      </ColumnItem>
      <RowData title="환불 수" content={`${unallocatedShares?.toLocaleString()}주`} />
      <RowData title="작품 위치" content={storagelocation ?? ''} />
      <DivideLine />
      <ColumnItem $gap={8}>
        <RowBetween $gap={8}>
          {activeProductSale?.files
            ?.filter(
              ({ productSaleFileItemType }) =>
                productSaleFileItemType === 'DOCS_INVESTOR_MANUAL' ||
                productSaleFileItemType === 'DOCS_INVESTMENT_CONTRACT',
            )
            .map(({ fileItemId, productSaleFileItemLabel, productSaleFileItemType }) => {
              const download = () => downloadProductFile(activeProductSale, productSaleFileItemType)
              return (
                <DownloadButton key={fileItemId} onClick={download}>
                  {productSaleFileItemLabel}
                </DownloadButton>
              )
            })}
        </RowBetween>
        <DownloadButton onClick={() => downloadProductFile(activeProductSale, 'DOCS_INVESTOR_LIST')}>
          투자자 명부
        </DownloadButton>
      </ColumnItem>
      <Button type="default" size="large" href={`/myPage/asset/${id}/saleInfo`}>
        매각 정보 확인하기
      </Button>
    </>
  )
}

/**
 * 정산 예정
 * @param UserProductDto
 */
const SettlementInfo = ({ id, totalShares, balance }: components['schemas']['UserProductDto']) => {
  return (
    <>
      <RowData title="보유 수량" content={`${totalShares?.toLocaleString()}주`} />
      <RowData title="정산 예정 금액" content={`${(balance ?? 0).toLocaleString()}원`} />
      <Button type="primary" size="large" href={`/myPage/asset/${id}/calculate`} style={{ marginTop: '8px' }}>
        매각 대금 정산하기
      </Button>
    </>
  )
}

/**
 * 정산 완료
 * @param UserProductDto
 */
const CompleteSettlementInfo = ({ id, totalShares, balance }: components['schemas']['UserProductDto']) => {
  return (
    <>
      <RowData title="보유 수량" content={`${totalShares?.toLocaleString()}주`} />
      <RowData title="정산 금액" content={`${(balance ?? 0).toLocaleString()}원`} />
      <Button type="default" size="large" href={`/myPage/asset/${id}/settlement`}>
        정산 내역 확인하기
      </Button>
    </>
  )
}
